import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/authStore";
import { filesApi } from "../api/filesApi";
import { podcastsApi } from "../api/podcastsApi";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useNotifications } from "../notifications/notificationsStore";
import { isAegisEmbedUrl } from "../podcasts/aegisPlayer";
import { useToast } from "../ui/toast/ToastProvider";
import { getSiteOrigin, joinUrl } from "../utils/seo";

const MAX_AUDIO_SIZE = 500 * 1024 * 1024;
const MAX_COVER_SIZE = 5 * 1024 * 1024;
const DEFAULT_TIMECODES = [{ time: "00:00", title: "Вступление" }];
const TOPIC_OPTIONS = ["Тревога", "Отношения", "Карьера", "Самопознание", "Травма", "Семья", "Депрессия"];
const AEGIS_WAIT_MESSAGES = [
  "Начищаем пластинки…",
  "Расклеиваем постеры…",
  "Мотаем кассету на карандаше…",
  "Сводим дорожки в студии…",
  "Проверяем тайм-коды и главы…",
  "Настраиваем ламповый усилитель…",
  "Разогреваем виниловый проигрыватель…",
  "Собираем плейлист для релиза…",
  "Чистим шумы и лишние паузы…",
  "Выравниваем громкость дорожки…",
  "Ловим удачные хайлайты выпуска…",
  "Готовим обложку к премьере…",
  "Перематываем лучшие моменты…",
  "Проверяем финальный мастер…",
  "Упаковываем эпизод для слушателей…",
  "Ставим метки на самые важные мысли…",
  "Готовим красивую карточку эпизода…",
];
const AEGIS_POLL_INTERVAL_MS = 12000;

function isAegisNotConfiguredError(err) {
  const code = String(err?.code || "").trim().toUpperCase();
  const msg = String(err?.message || "").trim().toUpperCase();
  return code === "AEGIS_NOT_CONFIGURED" || msg.includes("AEGIS_NOT_CONFIGURED");
}

function normalizeProcessingStatus(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";
  if (["READY", "COMPLETED", "SUCCESS", "DONE", "PUBLISHED"].includes(raw)) return "READY";
  if (["FAILED", "ERROR"].includes(raw)) return "FAILED";
  if (["PENDING", "PROCESSING", "QUEUED", "CREATED"].includes(raw)) return "PROCESSING";
  return raw;
}

function isAegisInProgress(status) {
  return normalizeProcessingStatus(status) === "PROCESSING";
}

function extractAegisStatus(payload = {}) {
  const raw = asObject(payload);
  const aegis = asObject(raw.aegis || raw.aegisData || raw.processing || raw.result);
  const candidate = firstNonEmpty(
    raw.processingStatus,
    raw.status,
    raw.aegisStatus,
    raw.state,
    aegis.processingStatus,
    aegis.status,
    aegis.state
  );
  return normalizeProcessingStatus(candidate);
}

function extractAegisError(payload = {}) {
  const raw = asObject(payload);
  const aegis = asObject(raw.aegis || raw.aegisData || raw.processing || raw.result);
  const nestedError = asObject(raw.error || aegis.error);
  return asText(
    raw.processingError ||
    raw.errorMessage ||
    raw.message ||
    aegis.processingError ||
    aegis.errorMessage ||
    aegis.message ||
    nestedError.message ||
    nestedError.detail,
    500
  );
}

function asText(value, max = 2000) {
  return String(value || "").trim().slice(0, max);
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toMinutes(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.min(600, Math.round(n)) : 0;
}

function unwrapPagePayload(payload) {
  const raw = asObject(payload);
  return raw.item ?? raw.items ?? raw.data ?? raw;
}

function normalizePage(payload) {
  const unwrapped = unwrapPagePayload(payload);
  if (Array.isArray(unwrapped)) return unwrapped;
  const page = asObject(unwrapped);
  return asArray(page.content || page.items || page.data || page.records);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = asText(value, 500);
    if (text) return text;
  }
  return "";
}

function initials(name) {
  const parsed = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0])
    .join("");
  return parsed ? parsed.toUpperCase() : "BS";
}

function normalizePodcastOption(item = {}) {
  const raw = asObject(item);
  return {
    id: raw.id ?? raw.podcastId ?? "",
    slug: firstNonEmpty(raw.slug),
    title: firstNonEmpty(raw.title, raw.name, "Подкаст"),
    coverEmoji: firstNonEmpty(raw.coverEmoji, raw.emoji, "🎙️"),
    coverImageUrl: firstNonEmpty(raw.coverImageUrl, raw.imageUrl, raw.coverUrl, raw.artworkUrl),
    shortDescription: firstNonEmpty(raw.shortDescription, raw.description, "Описание подкаста пока не заполнено."),
    episodesCount: toNumber(raw.episodesCount ?? raw.totalEpisodes, 0),
    published: raw.published !== undefined ? Boolean(raw.published) : true,
  };
}

function getEpisodePodcastId(episode) {
  return episode?.podcastId || episode?.showId || episode?.podcast?.id || "";
}

function normalizeTags(raw = {}) {
  const tags = raw.tags || raw.topicTags || raw.hashtags || [];
  if (Array.isArray(tags)) return tags.map((item) => asText(item, 40)).filter(Boolean).slice(0, 12);
  return String(tags || "")
    .split(/[#,]/)
    .map((item) => asText(item, 40))
    .filter(Boolean)
    .slice(0, 12);
}

function normalizeTagsWithTopic(tags, topic) {
  const normalizedTopic = asText(topic, 40).replace(/^#+/, "");
  const merged = [];
  if (normalizedTopic) merged.push(normalizedTopic);
  asArray(tags).forEach((item) => {
    const normalized = asText(item, 40).replace(/^#+/, "");
    if (!normalized) return;
    if (merged.some((value) => value.toLowerCase() === normalized.toLowerCase())) return;
    merged.push(normalized);
  });
  return merged.slice(0, 12);
}

function normalizeTimecodes(raw = {}) {
  const source = raw.timecodes || raw.chapters || raw.moments || raw.timestamps || [];
  const normalized = asArray(source)
    .map((item) => {
      if (typeof item === "string") {
        const [time, ...rest] = item.split(/\s+[-–—]\s+/);
        return { time: normalizeTimecode(time), title: asText(rest.join(" — "), 120) };
      }
      const obj = asObject(item);
      const normalizedFromSeconds = formatTimecodeFromSeconds(
        toSecondsFromTimecode(obj.timeSeconds ?? obj.seconds ?? obj.timeSec ?? obj.startSec)
      );
      return {
        time: normalizeTimecode(obj.time || obj.timestamp || obj.start || obj.startTime || normalizedFromSeconds),
        title: asText(obj.title || obj.label || obj.description || obj.text, 120),
      };
    })
    .filter((item) => item.time || item.title)
    .slice(0, 30);
  return normalized.length ? normalized : DEFAULT_TIMECODES;
}

function normalizeTimecode(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const cleaned = text.replace(/[^\d:]/g, "").slice(0, 8);
  if (!cleaned) return "";
  const parts = cleaned.split(":").filter((part) => part !== "");
  if (parts.length === 0) return "";
  if (parts.length === 1) {
    const onlyDigits = parts[0].replace(/\D/g, "");
    if (!onlyDigits) return "";
    if (onlyDigits.length <= 2) return `00:${onlyDigits.padStart(2, "0")}`;
    if (onlyDigits.length <= 4) {
      const mm = onlyDigits.slice(0, -2);
      const ss = onlyDigits.slice(-2);
      return `${mm}:${ss}`;
    }
    const hh = onlyDigits.slice(0, -4);
    const mm = onlyDigits.slice(-4, -2);
    const ss = onlyDigits.slice(-2);
    return `${hh}:${mm}:${ss}`;
  }
  return parts
    .slice(0, 3)
    .map((part, index) => (index === 0 ? part.slice(0, 3) : part.slice(0, 2).padStart(2, "0")))
    .join(":");
}

function toSecondsFromTimecode(value) {
  if (value === null || value === undefined || value === "") return null;
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d+$/.test(text)) return Number(text);
  const parts = text.split(":").map((chunk) => Number(chunk));
  if (parts.some((chunk) => !Number.isFinite(chunk))) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

function formatTimecodeFromSeconds(value) {
  const sec = Number(value);
  if (!Number.isFinite(sec) || sec < 0) return "";
  const total = Math.floor(sec);
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  if (hh > 0) return `${hh}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function toDatetimeLocal(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function extractUploadedUrl(uploaded) {
  const raw = asObject(uploaded);
  return firstNonEmpty(
    raw.url,
    raw.fileUrl,
    raw.documentUrl,
    raw.imageUrl,
    raw.path,
    raw.file?.url,
    raw.file?.fileUrl,
    raw.item?.url,
    raw.item?.fileUrl,
    raw.item?.file?.url,
    raw.data?.url,
    raw.data?.fileUrl,
    raw.data?.file?.url
  );
}

function extractUploadedAegisMeta(uploaded) {
  const raw = asObject(uploaded);
  const item = asObject(raw.item || raw.data || raw.result);
  return {
    aegisJobId: firstNonEmpty(
      item.aegisJobId,
      item.podcastId,
      item.jobId,
      raw.aegisJobId,
      raw.podcastId,
      raw.jobId
    ),
    aegisAssetId: firstNonEmpty(
      item.aegisAssetId,
      item.assetId,
      raw.aegisAssetId,
      raw.assetId
    ),
    aegisProjectId: firstNonEmpty(
      item.aegisProjectId,
      item.projectId,
      raw.aegisProjectId,
      raw.projectId
    ),
  };
}

function episodeToForm(episode = {}) {
  const durationMinutes = episode.durationMinutes || episode.durationMin ||
    (episode.durationSeconds ? Math.round(Number(episode.durationSeconds) / 60) : "");
  const rawStatus = String(episode.status || (episode.published ? "PUBLISHED" : "DRAFT") || "DRAFT").toUpperCase();

  return {
    title: episode.title || "",
    description: episode.description || "",
    topic: firstNonEmpty(episode.topic, asArray(episode.topics)[0], asArray(episode.topicLabels)[0]),
    episodeNumber: episode.episodeNumber || episode.number || "",
    durationMinutes,
    status: rawStatus,
    audioUrl: episode.audioUrl || episode.sourceUrl || episode.playbackUrl || episode.streamUrl || episode.mediaUrl || "",
    coverUrl: episode.coverUrl || episode.coverImageUrl || episode.imageUrl || "",
    ctaEnabled: Boolean(episode.ctaEnabled),
    scheduledAt: toDatetimeLocal(episode.scheduledAt || episode.publishAt || episode.publishDate),
    tags: normalizeTags(episode),
    timecodes: normalizeTimecodes(episode),
  };
}

function resolveAegisAutofill(episode = {}) {
  const root = asObject(episode);
  const aegis = asObject(root.aegis || root.aegisData || root.aegisPayload || root.processingResult);
  const ai = asObject(root.ai || aegis.ai || aegis.analysis || root.analysis);
  const player = asObject(root.player || aegis.player);
  const durationSeconds = toNumber(
    root.durationSeconds ??
      root.duration_seconds ??
      aegis.durationSeconds ??
      aegis.duration_seconds ??
      ai.durationSeconds ??
      ai.duration_seconds,
    0
  );

  const chapterCandidates =
    root.timecodes ||
    root.chapters ||
    root.moments ||
    root.timestamps ||
    ai.chapters ||
    ai.timecodes ||
    aegis.chapters ||
    aegis.timecodes;
  const summary = firstNonEmpty(
    root.summary,
    root.aiSummary,
    root.shortSummary,
    ai.summary,
    ai.description,
    aegis.summary
  );
  const coverUrl = firstNonEmpty(
    root.coverUrl,
    root.coverImageUrl,
    root.imageUrl,
    root.posterUrl,
    root.poster_url,
    aegis.posterUrl,
    aegis.poster_url,
    player.posterUrl,
    player.poster_url
  );

  return {
    durationMinutes: durationSeconds > 0 ? Math.max(1, Math.round(durationSeconds / 60)) : 0,
    timecodes: normalizeTimecodes({ timecodes: chapterCandidates }),
    summary,
    coverUrl,
  };
}

function publishModeFromStatus(status) {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "PUBLISHED") return "now";
  if (normalized === "SCHEDULED") return "later";
  return "draft";
}

function fileSizeLabel(file) {
  const size = Number(file?.size || 0);
  if (!size) return "";
  if (size > 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} МБ`;
  return `${Math.max(1, Math.round(size / 1024))} КБ`;
}

function StatusCheck({ ok, children }) {
  return <li className={ok ? "is-ok" : ""}>{children}</li>;
}

export default function PsychologistPodcastEpisodeBuilder() {
  const { role, me, logout } = useAuth();
  const { unreadNotificationsCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const { podcastId: routePodcastId, episodeId: routeEpisodeId } = useParams();
  const toast = useToast();
  const audioInputRef = React.useRef(null);
  const coverInputRef = React.useRef(null);
  const aegisSyncInFlightRef = React.useRef(false);
  const aegisPollTimerRef = React.useRef(null);

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const canManage = role === "PSYCHOLOGIST" || role === "ADMIN" || role === "MODERATOR";
  const stateEpisode = location.state?.sourceEpisode || null;
  const sourceEpisodeId = routeEpisodeId || location.state?.sourceEpisodeId || stateEpisode?.id || null;
  const queryPodcastId = React.useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("podcastId") || "";
  }, [location.search]);
  const initialPodcastId =
    routePodcastId ||
    location.state?.podcastId ||
    queryPodcastId ||
    getEpisodePodcastId(stateEpisode) ||
    "";

  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(Boolean(sourceEpisodeId && !stateEpisode));
  const [podcastsLoading, setPodcastsLoading] = React.useState(true);
  const [podcastOptions, setPodcastOptions] = React.useState([]);
  const [selectedPodcastId, setSelectedPodcastId] = React.useState(initialPodcastId);
  const [form, setForm] = React.useState(() => episodeToForm(stateEpisode || {}));
  const [tagInput, setTagInput] = React.useState("");
  const [publishMode, setPublishMode] = React.useState(() => publishModeFromStatus(stateEpisode?.status || (stateEpisode?.published ? "PUBLISHED" : "DRAFT")));
  const [uploadingAudio, setUploadingAudio] = React.useState(false);
  const [uploadAudioProgress, setUploadAudioProgress] = React.useState(0);
  const [uploadingCover, setUploadingCover] = React.useState(false);
  const [audioFileName, setAudioFileName] = React.useState("");
  const [coverFileName, setCoverFileName] = React.useState("");
  const [runtimeEpisodeId, setRuntimeEpisodeId] = React.useState(sourceEpisodeId || null);
  const [aegisStatus, setAegisStatus] = React.useState("");
  const [aegisError, setAegisError] = React.useState("");
  const [aegisMessageIndex, setAegisMessageIndex] = React.useState(0);
  const [aegisUnavailable, setAegisUnavailable] = React.useState(false);
  const [aegisAutofillEnabled, setAegisAutofillEnabled] = React.useState(true);
  const [uploadedAegisRef, setUploadedAegisRef] = React.useState({
    aegisJobId: "",
    aegisAssetId: "",
    aegisProjectId: "",
  });
  const autosaveTimerRef = React.useRef(null);
  const autosaveInFlightRef = React.useRef(false);
  const lastAutosaveSnapshotRef = React.useRef("");

  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(() => joinUrl(origin, selectedPodcastId ? `/dashboard/podcasts/${encodeURIComponent(String(selectedPodcastId))}/episodes/${sourceEpisodeId ? `${encodeURIComponent(String(sourceEpisodeId))}/edit` : "new"}` : "/dashboard/podcasts/new"), [origin, selectedPodcastId, sourceEpisodeId]);

  React.useEffect(() => {
    if (!canManage) {
      setPodcastsLoading(false);
      return undefined;
    }

    let alive = true;
    setPodcastsLoading(true);
    podcastsApi
      .getMyPodcasts({ page: 0, size: 100 })
      .then((payload) => {
        if (!alive) return;
        const options = normalizePage(payload)
          .map(normalizePodcastOption)
          .filter((item) => item.id || item.slug || item.title);

        setPodcastOptions(options);
        setSelectedPodcastId((current) => {
          if (current) return current;
          return options[0]?.id || "";
        });
      })
      .catch(() => {
        if (!alive) return;
        setPodcastOptions([]);
      })
      .finally(() => {
        if (alive) setPodcastsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [canManage]);

  const selectedPodcast = React.useMemo(
    () => podcastOptions.find((item) => String(item.id) === String(selectedPodcastId)) || null,
    [podcastOptions, selectedPodcastId]
  );
  const aegisIsProcessing = isAegisInProgress(aegisStatus);

  const hydrateEditorFromEpisode = React.useCallback((episode, mergeAegisOnly = false) => {
    if (!episode) return;
    const nextForm = episodeToForm(episode);
    const nextPodcastId = getEpisodePodcastId(episode);
    if (nextPodcastId) setSelectedPodcastId(nextPodcastId);
    setRuntimeEpisodeId(episode?.id || sourceEpisodeId || null);
    setAegisStatus(normalizeProcessingStatus(episode?.processingStatus || episode?.status));
    setAegisError(asText(episode?.processingError || episode?.error, 400));
    setAegisUnavailable(false);
    setAegisAutofillEnabled(episode?.aegisAutofillEnabled !== false);
    setUploadedAegisRef({
      aegisJobId: firstNonEmpty(episode?.aegisJobId, episode?.jobId),
      aegisAssetId: firstNonEmpty(episode?.aegisAssetId, episode?.assetId),
      aegisProjectId: firstNonEmpty(episode?.aegisProjectId, episode?.projectId),
    });

    if (mergeAegisOnly) {
      const aegisPatch = resolveAegisAutofill(episode);
      setForm((current) => ({
        ...current,
        durationMinutes: aegisPatch.durationMinutes || nextForm.durationMinutes || current.durationMinutes,
        timecodes: asArray(aegisPatch.timecodes).length
          ? aegisPatch.timecodes
          : (asArray(nextForm.timecodes).length ? nextForm.timecodes : current.timecodes),
        audioUrl: current.audioUrl || nextForm.audioUrl,
        title: current.title || nextForm.title,
        description: current.description || aegisPatch.summary || nextForm.description,
        coverUrl: current.coverUrl || aegisPatch.coverUrl || nextForm.coverUrl,
      }));
      return;
    }

    setForm(nextForm);
    setPublishMode(publishModeFromStatus(nextForm.status));
  }, [sourceEpisodeId]);

  const loadOwnEpisodeIntoEditor = React.useCallback(async (episodeId, mergeAegisOnly = false) => {
    if (!episodeId) return null;
    const episode = await podcastsApi.getMyPodcastEpisode(episodeId);
    if (!episode) return null;
    hydrateEditorFromEpisode(episode, mergeAegisOnly);
    return episode;
  }, [hydrateEditorFromEpisode]);

  const syncEpisodeAegisNow = React.useCallback(async (episodeId, { showReadyToast = true, showFailedToast = true, applyAutofill = true } = {}) => {
    if (!episodeId) return "";
    const sync = await podcastsApi.syncMyPodcastEpisodeAegis(episodeId, { timeoutMs: 90000 });
    if (!sync) return "";

    const nextStatus = extractAegisStatus(sync);
    const nextError = extractAegisError(sync);
    setAegisStatus(nextStatus || "PROCESSING");
    setAegisError(nextError);

    if (applyAutofill && nextStatus !== "FAILED") {
      await loadOwnEpisodeIntoEditor(episodeId, true);
    }

    if (nextStatus === "READY") {
      if (showReadyToast) {
        toast.success(
          applyAutofill
            ? "Aegis завершил обработку: длительность, главы и метаданные готовы."
            : "Aegis завершил обработку аудио."
        );
      }
      return "READY";
    }
    if (nextStatus === "FAILED") {
      if (showFailedToast) toast.error(nextError || "Aegis не смог обработать выпуск.");
      return "FAILED";
    }
    return nextStatus || "PROCESSING";
  }, [loadOwnEpisodeIntoEditor, toast]);

  React.useEffect(() => {
    if (!sourceEpisodeId) return undefined;
    let alive = true;
    setLoading(true);
    loadOwnEpisodeIntoEditor(sourceEpisodeId, false)
      .catch((err) => {
        if (!alive) return;
        toast.error(err?.message || "Не удалось загрузить эпизод");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [loadOwnEpisodeIntoEditor, sourceEpisodeId, toast]);

  React.useEffect(() => {
    setRuntimeEpisodeId(sourceEpisodeId || null);
  }, [sourceEpisodeId]);

  React.useEffect(() => {
    if (!aegisIsProcessing) return undefined;
    const timer = window.setInterval(() => {
      setAegisMessageIndex((current) => (current + 1) % AEGIS_WAIT_MESSAGES.length);
    }, 2200);
    return () => window.clearInterval(timer);
  }, [aegisIsProcessing]);

  React.useEffect(() => {
    if (!runtimeEpisodeId || !aegisIsProcessing || aegisUnavailable || uploadingAudio || saving) return undefined;
    let cancelled = false;

    const scheduleNext = (ms = AEGIS_POLL_INTERVAL_MS) => {
      if (cancelled) return;
      if (aegisPollTimerRef.current) window.clearTimeout(aegisPollTimerRef.current);
      aegisPollTimerRef.current = window.setTimeout(() => {
        void syncOnce();
      }, ms);
    };

    const syncOnce = async () => {
      if (cancelled) return;
      if (aegisSyncInFlightRef.current) {
        scheduleNext(4000);
        return;
      }
      aegisSyncInFlightRef.current = true;
      try {
        const nextStatus = await syncEpisodeAegisNow(runtimeEpisodeId, {
          showReadyToast: true,
          showFailedToast: true,
          applyAutofill: aegisAutofillEnabled,
        });
        if (cancelled) return;
        if (nextStatus === "READY") {
          return;
        }
        if (nextStatus === "FAILED") {
          return;
        }
      } catch (err) {
        if (!cancelled) {
          if (isAegisNotConfiguredError(err)) {
            setAegisStatus("FAILED");
            setAegisError("Aegis не настроен на backend (AEGIS_ENABLED/AEGIS_BASE_URL/AEGIS_PROJECT_TOKEN).");
            setAegisUnavailable(true);
            return;
          }
          const msg = asText(err?.message, 300) || "Не удалось обновить статус Aegis";
          setAegisError(msg);
        }
      } finally {
        aegisSyncInFlightRef.current = false;
      }
      scheduleNext(AEGIS_POLL_INTERVAL_MS);
    };

    void syncOnce();
    return () => {
      cancelled = true;
      aegisSyncInFlightRef.current = false;
      if (aegisPollTimerRef.current) {
        window.clearTimeout(aegisPollTimerRef.current);
        aegisPollTimerRef.current = null;
      }
    };
  }, [runtimeEpisodeId, aegisIsProcessing, aegisUnavailable, uploadingAudio, saving, syncEpisodeAegisNow, toast, aegisAutofillEnabled]);

  const setField = (name) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
    if (name === "audioUrl") {
      setUploadedAegisRef({ aegisJobId: "", aegisAssetId: "", aegisProjectId: "" });
    }
  };

  const addTag = React.useCallback((value = tagInput) => {
    const normalized = asText(value, 40).replace(/^#+/, "");
    if (!normalized) return;
    setForm((current) => {
      const next = [...(current.tags || [])];
      if (!next.some((item) => item.toLowerCase() === normalized.toLowerCase())) next.push(normalized);
      return { ...current, tags: next.slice(0, 12) };
    });
    setTagInput("");
  }, [tagInput]);

  const removeTag = (tag) => {
    setForm((current) => ({ ...current, tags: asArray(current.tags).filter((item) => item !== tag) }));
  };

  const updateTimecode = (index, key, value) => {
    setForm((current) => ({
      ...current,
      timecodes: asArray(current.timecodes).map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, [key]: key === "time" ? normalizeTimecode(value) : value }
          : item
      ),
    }));
  };

  const addTimecode = () => {
    setForm((current) => ({
      ...current,
      timecodes: [...asArray(current.timecodes), { time: "", title: "" }].slice(0, 30),
    }));
  };

  const removeTimecode = (index) => {
    setForm((current) => {
      const next = asArray(current.timecodes).filter((_, itemIndex) => itemIndex !== index);
      return { ...current, timecodes: next.length ? next : DEFAULT_TIMECODES };
    });
  };

  const uploadAudioFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_AUDIO_SIZE) {
      toast.error("Аудиофайл больше 500 МБ");
      return;
    }
    setUploadingAudio(true);
    setUploadAudioProgress(0);
    setAegisUnavailable(false);
    setAegisStatus("");
    setAegisError("");
    setAudioFileName(`${file.name}${fileSizeLabel(file) ? ` · ${fileSizeLabel(file)}` : ""}`);
    try {
      const uploaded = await filesApi.uploadAudio(file, {
        onProgress: (pct) => setUploadAudioProgress(pct),
      });
      const uploadedUrl = extractUploadedUrl(uploaded);
      if (!uploadedUrl) throw new Error("Сервер не вернул URL файла");
      const nextAegisRef = extractUploadedAegisMeta(uploaded);
      setUploadedAegisRef(nextAegisRef);
      const fallbackTitle = asText(form.title, 180) || asText(file?.name?.replace(/\.[^.]+$/, ""), 180) || "Новый эпизод";
      setForm((current) => ({
        ...current,
        title: current.title || fallbackTitle,
        audioUrl: uploadedUrl,
      }));
      toast.success("Аудиофайл загружен, готовим выпуск к обработке в Aegis…");

      const targetPodcastId = selectedPodcastId || initialPodcastId || getEpisodePodcastId(stateEpisode) || "";
      const draftDurationMinutes = toMinutes(form.durationMinutes);
      const cleanTags = normalizeTagsWithTopic(form.tags, form.topic);
      const cleanTimecodes = asArray(form.timecodes)
        .map((item) => {
          const normalizedTime = normalizeTimecode(item.time);
          const title = asText(item.title, 160);
          const timeSeconds = toSecondsFromTimecode(normalizedTime);
          return {
            time: normalizedTime,
            timeSeconds: Number.isFinite(timeSeconds) ? timeSeconds : null,
            seconds: Number.isFinite(timeSeconds) ? timeSeconds : null,
            title,
          };
        })
        .filter((item) => item.timeSeconds !== null && item.title);

      const draftPayload = {
        podcastId: targetPodcastId || undefined,
        title: fallbackTitle,
        description: asText(form.description, 4000),
        topic: asText(form.topic, 80) || null,
        episodeNumber: form.episodeNumber ? Math.max(1, Number(form.episodeNumber) || 0) : null,
        durationMinutes: draftDurationMinutes,
        durationSeconds: draftDurationMinutes > 0 ? draftDurationMinutes * 60 : null,
        status: "DRAFT",
        published: false,
        publishAfterReady: false,
        sourceUrl: uploadedUrl,
        audioUrl: uploadedUrl,
        playbackUrl: uploadedUrl,
        coverUrl: asText(form.coverUrl, 1000) || null,
        coverImageUrl: asText(form.coverUrl, 1000) || null,
        tags: cleanTags,
        chapters: cleanTimecodes,
        timecodes: cleanTimecodes,
        ctaEnabled: Boolean(form.ctaEnabled),
        aegisAutofillEnabled: Boolean(aegisAutofillEnabled),
        aegisJobId: nextAegisRef.aegisJobId || null,
        aegisAssetId: nextAegisRef.aegisAssetId || null,
        aegisProjectId: nextAegisRef.aegisProjectId || null,
      };

      const currentEpisodeId = runtimeEpisodeId || sourceEpisodeId || null;
      if (!currentEpisodeId && !targetPodcastId) {
        throw new Error("Сначала выберите подкаст, чтобы запустить обработку в Aegis");
      }

      const upsertedEpisode = currentEpisodeId
        ? await podcastsApi.updateMyPodcastEpisode(currentEpisodeId, draftPayload, { timeoutMs: 120000 })
        : await podcastsApi.createMyPodcastEpisode(targetPodcastId, draftPayload, { timeoutMs: 120000 });
      const nextEpisodeId = upsertedEpisode?.id || upsertedEpisode?.episodeId || currentEpisodeId || null;
      if (!nextEpisodeId) {
        throw new Error("Не удалось создать эпизод для обработки в Aegis");
      }
      setRuntimeEpisodeId(nextEpisodeId);
      const shouldNavigateToEdit = !currentEpisodeId && !sourceEpisodeId && targetPodcastId;

      const nextStatus = extractAegisStatus(upsertedEpisode) || "PROCESSING";
      const nextError = extractAegisError(upsertedEpisode);
      setAegisStatus(nextStatus);
      setAegisError(nextError);
      if (nextStatus === "FAILED") {
        toast.error(nextError || "Aegis не смог обработать выпуск.");
      } else {
        setAegisStatus("PROCESSING");
        toast.info("Файл загружен в Aegis. Началась обработка аудио.");
      }
      lastAutosaveSnapshotRef.current = "";

      if (shouldNavigateToEdit) {
        navigate(
          `/dashboard/podcasts/${encodeURIComponent(String(targetPodcastId))}/episodes/${encodeURIComponent(String(nextEpisodeId))}/edit`,
          { replace: true }
        );
      }
    } catch (err) {
      if (isAegisNotConfiguredError(err)) {
        setAegisStatus("FAILED");
        setAegisError("Aegis не настроен на backend (AEGIS_ENABLED/AEGIS_BASE_URL/AEGIS_PROJECT_TOKEN).");
        setAegisUnavailable(true);
      }
      toast.error(err?.message || "Не удалось загрузить аудиофайл");
    } finally {
      setUploadingAudio(false);
      setUploadAudioProgress(0);
    }
  };

  const uploadCoverFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_COVER_SIZE) {
      toast.error("Обложка больше 5 МБ");
      return;
    }
    setUploadingCover(true);
    setCoverFileName(`${file.name}${fileSizeLabel(file) ? ` · ${fileSizeLabel(file)}` : ""}`);
    try {
      const uploaded = await filesApi.uploadImage(file);
      const uploadedUrl = extractUploadedUrl(uploaded);
      if (!uploadedUrl) throw new Error("Сервер не вернул URL изображения");
      setForm((current) => ({ ...current, coverUrl: uploadedUrl }));
      toast.success("Обложка загружена");
    } catch (err) {
      toast.error(err?.message || "Не удалось загрузить обложку");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAudioDrop = (event) => {
    event.preventDefault();
    uploadAudioFile(event.dataTransfer?.files?.[0]);
  };

  const buildPayload = React.useCallback((nextStatus) => {
    const requestedStatus = String(nextStatus || form.status || "DRAFT").toUpperCase();
    const durationMinutes = toMinutes(form.durationMinutes);
    const audioUrl = asText(form.audioUrl, 1000) || null;
    const cleanTags = normalizeTagsWithTopic(form.tags, form.topic);
    const cleanTimecodes = asArray(form.timecodes)
      .map((item) => {
        const normalizedTime = normalizeTimecode(item.time);
        const title = asText(item.title, 160);
        const timeSeconds = toSecondsFromTimecode(normalizedTime);
        return {
          time: normalizedTime,
          timeSeconds: Number.isFinite(timeSeconds) ? timeSeconds : null,
          seconds: Number.isFinite(timeSeconds) ? timeSeconds : null,
          title,
        };
      })
      .filter((item) => item.timeSeconds !== null && item.title);

    return {
      podcastId: selectedPodcastId || undefined,
      title: asText(form.title, 180),
      description: asText(form.description, 4000),
      topic: asText(form.topic, 80) || null,
      episodeNumber: form.episodeNumber ? Math.max(1, Number(form.episodeNumber) || 0) : null,
      durationMinutes,
      durationSeconds: durationMinutes > 0 ? durationMinutes * 60 : null,
      status: requestedStatus,
      published: requestedStatus === "PUBLISHED",
      publishAfterReady: requestedStatus === "PUBLISHED",
      scheduledAt: publishMode === "later" ? form.scheduledAt || null : null,
      sourceUrl: audioUrl,
      audioUrl,
      playbackUrl: audioUrl,
      coverUrl: asText(form.coverUrl, 1000) || null,
      coverImageUrl: asText(form.coverUrl, 1000) || null,
      tags: cleanTags,
      timecodes: cleanTimecodes,
      chapters: cleanTimecodes,
      ctaEnabled: Boolean(form.ctaEnabled),
      aegisAutofillEnabled: Boolean(aegisAutofillEnabled),
      aegisJobId: uploadedAegisRef.aegisJobId || null,
      aegisAssetId: uploadedAegisRef.aegisAssetId || null,
      aegisProjectId: uploadedAegisRef.aegisProjectId || null,
    };
  }, [aegisAutofillEnabled, form, publishMode, selectedPodcastId, uploadedAegisRef]);

  const autosaveSnapshot = React.useMemo(() => JSON.stringify({
    runtimeEpisodeId: runtimeEpisodeId || "",
    selectedPodcastId: selectedPodcastId || "",
    publishMode: publishMode || "",
    aegisAutofillEnabled: Boolean(aegisAutofillEnabled),
    uploadedAegisRef,
    form,
  }), [aegisAutofillEnabled, form, publishMode, runtimeEpisodeId, selectedPodcastId, uploadedAegisRef]);

  React.useEffect(() => {
    if (!runtimeEpisodeId || loading || podcastsLoading || uploadingAudio || uploadingCover || saving) {
      return undefined;
    }
    if (autosaveSnapshot === lastAutosaveSnapshotRef.current) {
      return undefined;
    }

    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = window.setTimeout(async () => {
      if (autosaveInFlightRef.current) return;
      autosaveInFlightRef.current = true;
      try {
        const payload = buildPayload(form.status || "DRAFT");
        const savedEpisode = await podcastsApi.updateMyPodcastEpisode(runtimeEpisodeId, payload, {
          timeoutMs: 25000,
          keepalive: true,
        });
        lastAutosaveSnapshotRef.current = autosaveSnapshot;
        setAegisStatus(normalizeProcessingStatus(savedEpisode?.processingStatus || savedEpisode?.status));
        setAegisError(asText(savedEpisode?.processingError || savedEpisode?.error, 400));
        setRuntimeEpisodeId(savedEpisode?.id || savedEpisode?.episodeId || runtimeEpisodeId);
      } catch {
      } finally {
        autosaveInFlightRef.current = false;
      }
    }, 1500);

    return () => {
      if (autosaveTimerRef.current) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [
    autosaveSnapshot,
    buildPayload,
    form.status,
    loading,
    podcastsLoading,
    runtimeEpisodeId,
    saving,
    uploadingAudio,
    uploadingCover,
  ]);

  const resolvePodcastBackPath = React.useCallback(
    () => (selectedPodcastId ? `/dashboard/podcasts/${encodeURIComponent(String(selectedPodcastId))}/edit` : "/dashboard/podcasts"),
    [selectedPodcastId]
  );

  const flushDraftAutosave = React.useCallback(async () => {
    if (!runtimeEpisodeId || loading || podcastsLoading || uploadingAudio || uploadingCover || saving) {
      return;
    }
    if (autosaveSnapshot === lastAutosaveSnapshotRef.current || autosaveInFlightRef.current) {
      return;
    }
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    autosaveInFlightRef.current = true;
    try {
      const payload = buildPayload(form.status || "DRAFT");
      const savedEpisode = await podcastsApi.updateMyPodcastEpisode(runtimeEpisodeId, payload, {
        timeoutMs: 25000,
      });
      lastAutosaveSnapshotRef.current = autosaveSnapshot;
      setAegisStatus(normalizeProcessingStatus(savedEpisode?.processingStatus || savedEpisode?.status));
      setAegisError(asText(savedEpisode?.processingError || savedEpisode?.error, 400));
      setRuntimeEpisodeId(savedEpisode?.id || savedEpisode?.episodeId || runtimeEpisodeId);
    } catch {
    } finally {
      autosaveInFlightRef.current = false;
    }
  }, [
    autosaveSnapshot,
    buildPayload,
    form.status,
    loading,
    podcastsLoading,
    runtimeEpisodeId,
    saving,
    uploadingAudio,
    uploadingCover,
  ]);

  const handleExitToPodcast = React.useCallback(async () => {
    await flushDraftAutosave();
    navigate(resolvePodcastBackPath());
  }, [flushDraftAutosave, navigate, resolvePodcastBackPath]);

  const handleExitToPodcastsList = React.useCallback(async () => {
    await flushDraftAutosave();
    navigate("/dashboard/podcasts");
  }, [flushDraftAutosave, navigate]);

  const save = async (mode = publishMode, options = {}) => {
    const shouldExitWhileProcessing = Boolean(options?.exitWhileProcessing);
    const normalizedMode = String(mode || "draft");
    const nextStatus = normalizedMode === "now" ? "PUBLISHED" : normalizedMode === "later" ? "SCHEDULED" : "DRAFT";
    const payload = buildPayload(nextStatus);
    if (!payload.title) {
      toast.error("Укажите название эпизода");
      return;
    }
    if (!payload.podcastId && !sourceEpisodeId) {
      toast.error("Выберите подкаст, к которому нужно добавить выпуск");
      return;
    }
    if (normalizedMode === "later" && !payload.scheduledAt) {
      toast.error("Укажите дату и время публикации");
      return;
    }

    setSaving(true);
    try {
      const savedEpisode = sourceEpisodeId
        ? await podcastsApi.updateMyPodcastEpisode(sourceEpisodeId, payload, { timeoutMs: 120000 })
        : await podcastsApi.createMyPodcastEpisode(payload.podcastId, payload, { timeoutMs: 120000 });
      const episodeId = savedEpisode?.id || savedEpisode?.episodeId || sourceEpisodeId || null;
      const processingStatus = normalizeProcessingStatus(savedEpisode?.processingStatus || savedEpisode?.status);
      const processingError = asText(savedEpisode?.processingError || savedEpisode?.error, 400);
      if (episodeId) setRuntimeEpisodeId(episodeId);

      setAegisStatus(processingStatus);
      setAegisError(processingError);
      lastAutosaveSnapshotRef.current = autosaveSnapshot;

      const message = normalizedMode === "now" ? "Эпизод опубликован" : normalizedMode === "later" ? "Эпизод запланирован" : "Черновик эпизода сохранён";
      toast.success(message);

      if (processingStatus === "PROCESSING") {
        toast.info(AEGIS_WAIT_MESSAGES[aegisMessageIndex % AEGIS_WAIT_MESSAGES.length] || "Aegis обрабатывает выпуск…");
        if (shouldExitWhileProcessing) {
          navigate(resolvePodcastBackPath());
          return;
        }
        if (!sourceEpisodeId && episodeId) {
          navigate(
            `/dashboard/podcasts/${encodeURIComponent(String(payload.podcastId))}/episodes/${encodeURIComponent(String(episodeId))}/edit`,
            { replace: true }
          );
        }
        return;
      }

      navigate(resolvePodcastBackPath());
    } catch (err) {
      const status = Number(err?.status);
      if (normalizedMode === "later" && (status === 400 || status === 422)) {
        try {
          const fallbackPayload = { ...payload, status: "DRAFT", published: false, publishAfterReady: false };
          if (sourceEpisodeId) await podcastsApi.updateMyPodcastEpisode(sourceEpisodeId, fallbackPayload, { timeoutMs: 120000 });
          else await podcastsApi.createMyPodcastEpisode(fallbackPayload.podcastId, fallbackPayload, { timeoutMs: 120000 });
          toast.success("Дата публикации сохранена, эпизод оставлен черновиком до поддержки расписания на сервере");
          navigate(resolvePodcastBackPath());
          return;
        } catch (fallbackErr) {
          toast.error(fallbackErr?.message || "Не удалось сохранить эпизод");
          return;
        }
      }
      toast.error(err?.message || "Не удалось сохранить эпизод");
    } finally {
      setSaving(false);
    }
  };

  const coverPreview = form.coverUrl || selectedPodcast?.coverImageUrl || "";
  const hasAudio = Boolean(form.audioUrl || audioFileName);
  const aegisEmbedUrl = React.useMemo(() => {
    const candidate = firstNonEmpty(form.audioUrl);
    return isAegisEmbedUrl(candidate) ? candidate : "";
  }, [form.audioUrl]);
  const shouldShowAegisPlayerOnly = Boolean(aegisEmbedUrl) && aegisStatus === "READY";
  const timecodes = asArray(form.timecodes).length ? asArray(form.timecodes) : DEFAULT_TIMECODES;
  const aegisStageLabel = uploadingAudio
    ? "Загрузка в Aegis"
    : aegisStatus === "READY"
      ? "Готово"
      : aegisStatus === "FAILED"
        ? "Ошибка обработки"
        : aegisIsProcessing
          ? "Обработка в Aegis"
          : "Ожидание";
  const isAwaitingAegisAfterUpload = uploadingAudio && Number(uploadAudioProgress) >= 100;
  const currentAegisWaitMessage = AEGIS_WAIT_MESSAGES[aegisMessageIndex % AEGIS_WAIT_MESSAGES.length] || "Aegis обрабатывает выпуск…";
  const showAegisProcessingHint = isAwaitingAegisAfterUpload || aegisIsProcessing;
  const aegisStageMessage = uploadingAudio
    ? (isAwaitingAegisAfterUpload ? "Файл передан на сервер. Ждём подтверждение от Aegis…" : `Загружаем файл в Aegis… ${uploadAudioProgress}%`)
    : aegisStatus === "READY"
      ? "Выпуск обработан: длительность, главы и метаданные синхронизированы."
      : aegisStatus === "FAILED"
        ? (aegisError || "Aegis вернул ошибку обработки.")
        : aegisIsProcessing
          ? `Файл загружен в Aegis, началась обработка. ${currentAegisWaitMessage}`
          : "Аудио будет обработано и доступно подписчикам после публикации.";

  if (!canManage) {
    return (
      <section className="dashboard-placeholder" style={{ marginBottom: 24 }}>
        <div className="dashboard-placeholder__hero">
          <h3>Доступ ограничен</h3>
          <p>Управление подкастами доступно только психологам и администраторам.</p>
        </div>
        <div className="dashboard-placeholder__actions">
          <Link className="dashboard__btn dashboard__btn--ghost" to="/dashboard">
            ← Вернуться в кабинет
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div className="podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>{sourceEpisodeId ? "Редактирование выпуска" : "Новый выпуск"} | Бюро Души</title>
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="podcasts"
          mobileOpen={sidebarOpen}
          userName={me?.name || me?.login || "Гость"}
          roleLabel={role || "PSYCHOLOGIST"}
          notificationsBadge={unreadNotificationsCount}
          onCloseMobile={() => setSidebarOpen(false)}
          onLogout={logout}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar">
            <div className="podcasts-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <div className="podcasts-topbar__search podcast-editor-v3__top-current">
                <span>{selectedPodcast ? `${selectedPodcast.coverEmoji} ${selectedPodcast.title}` : "Выберите подкаст для выпуска"}</span>
              </div>
            </div>
            <div className="podcasts-topbar__right">
              <button className="podcasts-btn podcasts-btn--subtle" type="button" onClick={handleExitToPodcast}>К подкасту</button>
              <button className="podcasts-btn podcasts-btn--mint" type="button" disabled={saving || loading || uploadingAudio || uploadingCover} onClick={() => save("now")}>{saving ? "Сохраняем..." : "Опубликовать"}</button>
              <div className="podcasts-topbar__separator" />
              <button className="podcasts-topbar__avatar" type="button" onClick={() => navigate("/dashboard/settings")}>{initials(me?.name || me?.login)}</button>
            </div>
          </header>

          <main className="podcasts-content podcast-editor-v3">
            <div className="podcast-editor-v3__crumbs">
              <button type="button" onClick={handleExitToPodcastsList}>Мои подкасты</button>
              <span aria-hidden="true">›</span>
              {selectedPodcastId ? (
                <>
                  <button type="button" onClick={handleExitToPodcast}>
                    {selectedPodcast?.title || "Подкаст"}
                  </button>
                  <span aria-hidden="true">›</span>
                </>
              ) : null}
              <span>{sourceEpisodeId ? "Редактирование эпизода" : "Новый эпизод"}</span>
            </div>

            <header className="podcast-editor-v3__head">
              <div>
                <h1>{sourceEpisodeId ? "Редактирование эпизода" : "Новый эпизод"}</h1>
                <p>
                  Загрузите аудио, заполните описание, добавьте теги и моменты выпуска. После публикации подписчики получат новый эпизод в разделе подкастов.
                </p>
              </div>
              <div className="podcast-editor-v3__head-actions">
                <button type="button" className="podcast-editor-v3__ghost-btn" disabled={saving || loading} onClick={() => save("draft")}>Сохранить черновик</button>
                <button type="button" className="podcast-editor-v3__mint-btn" disabled={saving || loading || uploadingAudio || uploadingCover} onClick={() => save(publishMode)}>{saving ? "Сохраняем..." : publishMode === "draft" ? "Сохранить" : publishMode === "later" ? "Запланировать" : "Опубликовать"}</button>
              </div>
            </header>

            {loading || podcastsLoading ? <div className="podcasts-empty podcasts-empty--inline">Загружаем данные редактора…</div> : null}

            <div className="podcast-editor-v3__layout">
              <div className="podcast-editor-v3__left">
                <section className="podcast-editor-v3__card">
                  <div className="podcast-editor-v3__section-title">Основное</div>

                  <label className="podcast-editor-v3__field">
                    <span>Подкаст</span>
                    <select
                      value={selectedPodcastId || ""}
                      onChange={(event) => setSelectedPodcastId(event.target.value)}
                      disabled={podcastsLoading || podcastOptions.length === 0 || Boolean(sourceEpisodeId)}
                    >
                      {podcastsLoading ? <option value="">Загружаем подкасты...</option> : null}
                      {!podcastsLoading && podcastOptions.length === 0 ? <option value="">Подкасты не найдены</option> : null}
                      {podcastOptions.map((podcast) => (
                        <option key={`podcast-option-${podcast.id || podcast.slug}`} value={podcast.id || ""}>
                          {podcast.coverEmoji} {podcast.title}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="podcast-editor-v3__field">
                    <span>Название эпизода</span>
                    <input value={form.title} onChange={setField("title")} placeholder="Например: Что делать, когда тревога накрывает" />
                  </label>

                  <label className="podcast-editor-v3__field">
                    <span>Описание</span>
                    <textarea value={form.description} onChange={setField("description")} rows={5} placeholder="Расскажите, о чём этот эпизод. Это поможет слушателям понять, стоит ли его открывать…" />
                  </label>

                  <div className="podcast-editor-v3__fields-row">
                    <label className="podcast-editor-v3__field">
                      <span>Тема</span>
                      <select value={form.topic || ""} onChange={setField("topic")}>
                        <option value="">Выберите тему</option>
                        {TOPIC_OPTIONS.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
                      </select>
                    </label>
                    <label className="podcast-editor-v3__field">
                      <span>Номер эпизода</span>
                      <input type="number" min="1" value={form.episodeNumber} onChange={setField("episodeNumber")} placeholder="25" />
                    </label>
                  </div>
                </section>

                <section className="podcast-editor-v3__card">
                  <div className="podcast-editor-v3__section-title">Аудиофайл</div>
                  {shouldShowAegisPlayerOnly ? (
                    <div className="ep-aegis-player-wrap">
                      <iframe
                        className="ep-aegis-player"
                        src={aegisEmbedUrl}
                        title="Aegis player preview"
                        loading="lazy"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : null}
                  {!shouldShowAegisPlayerOnly ? (
                    <>
                  <button
                    type="button"
                    className={`podcast-editor-v3__audio-drop ${hasAudio ? "has-file" : ""}`}
                    onClick={() => audioInputRef.current?.click()}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={handleAudioDrop}
                  >
                    <span className="podcast-editor-v3__upload-icon" aria-hidden="true">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="11" r="4"/><path d="M12 15v4M8 21h8"/><path d="M5 11a7 7 0 0 0 14 0" strokeLinecap="round"/></svg>
                    </span>
                    <strong>{uploadingAudio ? "Загружаем аудио…" : hasAudio ? "Аудиофайл готов" : "Загрузить аудиофайл"}</strong>
                    <small>{uploadingAudio ? `${uploadAudioProgress}%` : (audioFileName || (form.audioUrl ? "Файл уже указан ссылкой" : "MP3, WAV, M4A · до 500 МБ · можно перетащить сюда"))}</small>
                  </button>
                  <label className="podcast-editor-v3__aegis-autofill-check">
                    <input
                      type="checkbox"
                      checked={aegisAutofillEnabled}
                      onChange={(event) => setAegisAutofillEnabled(Boolean(event.target.checked))}
                    />
                    <span>Заполнить поля автоматически после обработки в Aegis (длительность, моменты, описание)</span>
                  </label>
                  {uploadingAudio ? (
                    <div className="podcast-editor-v3__upload-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploadAudioProgress}>
                      <span style={{ width: `${uploadAudioProgress}%` }} />
                    </div>
                  ) : null}
                  {showAegisProcessingHint ? (
                    <div className="podcast-editor-v3__aegis-inline" role="status" aria-live="polite">
                      <span className="podcast-editor-v3__aegis-inline-dot" aria-hidden="true" />
                      <span key={`aegis-wait-${aegisMessageIndex}`} className="podcast-editor-v3__aegis-inline-text">
                        {aegisIsProcessing ? `Файл загружен в Aegis. ${currentAegisWaitMessage}` : "Почти готово, ожидаем подтверждение от Aegis…"}
                      </span>
                    </div>
                  ) : null}
                  {showAegisProcessingHint ? (
                    <div className="podcast-editor-v3__aegis-choice" role="status" aria-live="polite">
                      <p>Вы можете сохранить черновик подкаста и вернуться позже, когда Aegis закончит обработку. Мы уведомим Вас о завершении или Вы можете дождаться завершения сами.</p>
                    </div>
                  ) : null}
                  <input
                    ref={audioInputRef}
                    type="file"
                    accept="audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,audio/aac,audio/ogg,.mp3,.wav,.m4a,.aac,.ogg"
                    hidden
                    onChange={(event) => uploadAudioFile(event.target.files?.[0])}
                  />
                  <label className="podcast-editor-v3__field podcast-editor-v3__field--compact">
                    <span>Или ссылка на аудио</span>
                    <input value={form.audioUrl} onChange={setField("audioUrl")} placeholder="https://.../episode.mp3" />
                  </label>
                  <label className="podcast-editor-v3__field podcast-editor-v3__field--compact">
                    <span>Длительность, минут</span>
                    <input type="number" min="0" max="600" value={form.durationMinutes} onChange={setField("durationMinutes")} />
                  </label>
                    </>
                  ) : null}
                </section>

                <section className="podcast-editor-v3__card">
                  <div className="podcast-editor-v3__section-title">Теги</div>
                  <div className="podcast-editor-v3__tags">
                    {asArray(form.tags).length === 0 ? <span className="podcast-editor-v3__tag-empty">Добавьте теги, чтобы выпуск легче находили в каталоге</span> : null}
                    {asArray(form.tags).map((tag) => (
                      <span className="podcast-editor-v3__tag" key={tag}>#{tag}<button type="button" onClick={() => removeTag(tag)} aria-label={`Удалить тег ${tag}`}>×</button></span>
                    ))}
                  </div>
                  <div className="podcast-editor-v3__tag-form">
                    <input
                      value={tagInput}
                      onChange={(event) => setTagInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Добавить тег..."
                    />
                    <button type="button" onClick={() => addTag()}>+ Добавить</button>
                  </div>
                </section>

                <section className="podcast-editor-v3__card">
                  <div className="podcast-editor-v3__section-title podcast-editor-v3__section-title--icon">
                    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="10" cy="10" r="7.5"/><path d="M10 6v4l2.5 2.5" strokeLinecap="round"/></svg>
                    Моменты выпуска
                  </div>
                  <p className="podcast-editor-v3__hint">Таймкоды помогают слушателям быстро перейти к нужной части эпизода.</p>
                  <div className="podcast-editor-v3__timecodes">
                    {timecodes.map((item, index) => (
                      <div className="podcast-editor-v3__timecode" key={`timecode-${index}`}>
                        <span className="podcast-editor-v3__drag" aria-hidden="true">⋮⋮</span>
                        <input
                          className="podcast-editor-v3__timecode-time"
                          value={item.time || ""}
                          maxLength={8}
                          placeholder="00:00"
                          onChange={(event) => updateTimecode(index, "time", event.target.value)}
                        />
                        <input
                          className="podcast-editor-v3__timecode-title"
                          value={item.title || ""}
                          placeholder="Описание момента..."
                          onChange={(event) => updateTimecode(index, "title", event.target.value)}
                        />
                        <button type="button" className="podcast-editor-v3__remove-btn" onClick={() => removeTimecode(index)} aria-label="Удалить момент">×</button>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="podcast-editor-v3__add-timecode" onClick={addTimecode}>+ Добавить момент</button>
                  <p className="podcast-editor-v3__micro-hint">Формат ММ:СС или ЧЧ:ММ:СС. Моменты отсортируются на сервере или при последующей обработке.</p>
                </section>
              </div>

              <aside className="podcast-editor-v3__right">
                <section className="podcast-editor-v3__card podcast-editor-v3__cover-card">
                  <div className="podcast-editor-v3__section-title">Обложка эпизода</div>
                  <button type="button" className={`podcast-editor-v3__cover-drop ${coverPreview ? "has-cover" : ""}`} onClick={() => coverInputRef.current?.click()}>
                    {coverPreview ? <img src={coverPreview} alt="Обложка эпизода" /> : (
                      <>
                        <span className="podcast-editor-v3__cover-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none"/><path d="M21 15l-5-5L5 21"/></svg></span>
                        <span>Загрузить изображение<br /><small>JPG, PNG · 1:1 · до 5 МБ</small></span>
                      </>
                    )}
                  </button>
                  <input ref={coverInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(event) => uploadCoverFile(event.target.files?.[0])} />
                  {coverFileName || uploadingCover ? <p className="podcast-editor-v3__micro-hint">{uploadingCover ? "Загружаем обложку…" : coverFileName}</p> : <p className="podcast-editor-v3__micro-hint">Если не загрузить, будет использована обложка подкаста.</p>}
                  <label className="podcast-editor-v3__field podcast-editor-v3__field--compact">
                    <span>Или ссылка на обложку</span>
                    <input value={form.coverUrl} onChange={setField("coverUrl")} placeholder="https://.../cover.webp" />
                  </label>
                </section>

                <section className="podcast-editor-v3__card podcast-editor-v3__publish-card">
                  <div className="podcast-editor-v3__section-title">Публикация</div>
                  <label className="podcast-editor-v3__radio">
                    <input type="radio" name="publishMode" value="now" checked={publishMode === "now"} onChange={() => setPublishMode("now")} />
                    <span><strong>Опубликовать сразу</strong><small>Эпизод появится сразу после сохранения</small></span>
                  </label>
                  <label className="podcast-editor-v3__radio">
                    <input type="radio" name="publishMode" value="later" checked={publishMode === "later"} onChange={() => setPublishMode("later")} />
                    <span><strong>Запланировать</strong><small>Выберите дату и время</small></span>
                  </label>
                  {publishMode === "later" ? (
                    <label className="podcast-editor-v3__field podcast-editor-v3__field--compact">
                      <span>Дата публикации</span>
                      <input type="datetime-local" value={form.scheduledAt} onChange={setField("scheduledAt")} />
                    </label>
                  ) : null}
                  <label className="podcast-editor-v3__radio">
                    <input type="radio" name="publishMode" value="draft" checked={publishMode === "draft"} onChange={() => setPublishMode("draft")} />
                    <span><strong>Сохранить черновик</strong><small>Вернитесь позже и опубликуйте вручную</small></span>
                  </label>
                  <label className="podcast-editor-v3__check">
                    <input type="checkbox" checked={form.ctaEnabled} onChange={setField("ctaEnabled")} />
                    <span>Показывать CTA на запись после прослушивания</span>
                  </label>
                  <div className="podcast-editor-v3__publish-actions">
                    <button className="podcast-editor-v3__mint-btn" type="button" disabled={saving || loading || uploadingAudio || uploadingCover} onClick={() => save(publishMode)}>{saving ? "Сохраняем..." : publishMode === "now" ? "Опубликовать" : publishMode === "later" ? "Запланировать" : "Сохранить"}</button>
                    <button className="podcast-editor-v3__ghost-btn" type="button" disabled={saving || loading} onClick={() => save("draft")}>Сохранить черновик</button>
                    <button className="podcast-editor-v3__plain-btn" type="button" onClick={handleExitToPodcast}>Отмена</button>
                  </div>
                </section>

                <section className="podcast-editor-v3__card podcast-editor-v3__checklist-card">
                  <div className="podcast-editor-v3__section-title">Проверка</div>
                  <ul>
                    <StatusCheck ok={selectedPodcastId || sourceEpisodeId}>Выпуск связан с подкастом</StatusCheck>
                    <StatusCheck ok={form.title}>Название заполнено</StatusCheck>
                    <StatusCheck ok={form.description}>Описание заполнено</StatusCheck>
                    <StatusCheck ok={form.audioUrl}>Аудиофайл указан</StatusCheck>
                    <StatusCheck ok={asArray(form.tags).length}>Добавлены теги</StatusCheck>
                  </ul>
                </section>

                <section className="podcast-editor-v3__aegis-card">
                  <div><span className={aegisStatus === "FAILED" ? "is-failed" : ""} /> Aegis · {aegisStageLabel}</div>
                  <p>{aegisStageMessage}</p>
                </section>
              </aside>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
